import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { put } from 'helpers/api';
import confirm from 'components/confirm';
import AddControl from 'components/add_control';
import { Select } from 'components/ui/select';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import QuickbooksSelect from './select';
import { loadVendorsById, loadNonBrokerUsers } from './api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

export default function VendorRelationTable({ users, relationType, showAll, vendorFetchType, usersCount }) {
  const userRows = showAll ? users : users.filter(u => u.quickbooksId);
  const initialRows = userRows.length === 0 && !showAll
    ? []
    : userRows.map(u => ({ id: `row-${u.id}`, userId: u.id, quickbooksId: u.quickbooksId }));
  const [rows, setRows] = useState(initialRows);
  const rowIdCounter = useRef(0);

  // The vendor map is resolved asynchronously (one batched request) rather than at page render, so a slow
  // QuickBooks callout doesn't block the settings tab from painting. We still fetch in bulk — never per
  // row — to stay under QuickBooks' 10-requests/sec limit. On failure we drop to per-row hydration.
  const anyLinked = users.some(u => u.quickbooksId);
  const [vendorsById, setVendorsById] = useState({});
  const [vendorsLoading, setVendorsLoading] = useState(anyLinked);
  const [vendorsError, setVendorsError] = useState(false);

  useEffect(() => {
    if (!anyLinked) return;
    loadVendorsById(
      vendorFetchType,
      (map) => {
        setVendorsById(map);
        setVendorsLoading(false);
      },
      () => {
        setVendorsError(true);
        setVendorsLoading(false);
      },
    );
  }, [vendorFetchType, anyLinked]);

  const [localUsers, setLocalUsers] = useState(users || []);
  const shouldOpenUserSelectModalOnLoad = rows.length === 0 && !showAll;
  const [showUserSelectModal, setShowUserSelectModal] = useState(shouldOpenUserSelectModalOnLoad);

  // The picker's full non-broker user list is loaded on demand (when the modal first opens) rather than
  // embedded in the page, so a large brokerage's user list isn't shipped on every render. `null` means
  // "not fetched yet".
  const [availableUsers, setAvailableUsers] = useState(null);
  const [availableUsersLoading, setAvailableUsersLoading] = useState(false);

  useEffect(() => {
    if (!showUserSelectModal || availableUsers != null || availableUsersLoading) return;
    setAvailableUsersLoading(true);
    loadNonBrokerUsers(
      (list) => {
        setAvailableUsers(list);
        setAvailableUsersLoading(false);
      },
      () => setAvailableUsersLoading(false),
    );
  }, [showUserSelectModal, availableUsers, availableUsersLoading]);

  const selectedUserIds = rows.map(r => r.userId).filter(Boolean);
  const canAddUsers = usersCount != null && selectedUserIds.length < usersCount;

  async function removeRow(rowId) {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;

    // If there's no associated QuickBooks ID, remove the row immediately
    // without prompting the user.
    if (!row.quickbooksId) {
      const updatedRows = rows.filter(r => r.id !== rowId);
      if (updatedRows.length === 0) {
        onNoRows();
        return;
      }
      setRows(updatedRows);
      return;
    }

    const confirmationMessage = 'Are you sure you want to delete this association? '
      + 'The vendor will remain in QuickBooks but they will be unlinked from this user.';
    try {
      await confirm(confirmationMessage, {
        title: `Delete Association?`,
        buttonLabel: 'Delete Association',
        buttonStyle: 'danger'
      });
    } catch (e) {
      return;
    }

    try {
      await updateUserQuickbooksId(row.userId, null);
      const updatedLocalUsers = localUsers.map(u => (u.id === row.userId ? { ...u, quickbooksId: null } : u));
      setLocalUsers(updatedLocalUsers);
      const updatedRows = rows.filter(r => r.id !== rowId);
      if (updatedRows.length === 0) {
        onNoRows();
        return;
      }

      setRows(updatedRows);
    } catch (e) {
      // Error handling for updateUserQuickbooksId failure
    }
  }

  function handleVendorChange(user, vendor) {
    const quickbooksId = vendor ? parseInt(vendor.id, 10) : null;
    updateUserQuickbooksId(user.id, quickbooksId).then(() => {
      setLocalUsers(prev => prev.map(u => (u.id === user.id ? { ...u, quickbooksId } : u)));
      setRows(prev => prev.map(r => (r.userId === user.id ? { ...r, quickbooksId } : r)));
    });
  }

  function updateUserQuickbooksId(userId, quickbooksId) {
    return put(`/users/${userId}`, { user: { quickbooks_id: quickbooksId } });
  }

  function onNoRows() {
    if (!showAll) {
      setRows([]);
    }
  }

  function renderUserSelectModal() {
    const selectedUserIds = rows.map(r => r.userId).filter(Boolean);

    function handleUserSelect(user) {
      if (user) {
        // The picked user comes from the on-demand list, so add it to localUsers too — row rendering and
        // optimistic updates look users up there.
        setLocalUsers(prev => (prev.some(u => u.id === user.id) ? prev : [...prev, user]));
        setRows((prev) => {
          rowIdCounter.current += 1;
          const newRowId = `new-row-${rowIdCounter.current}`;
          const updatedRows = [...prev, { id: newRowId, userId: user.id, quickbooksId: user.quickbooksId || null }];
          return updatedRows;
        });
      }
      setShowUserSelectModal(false);
    }

    return (
      <Modal open={true} onClose={() => setShowUserSelectModal(false)}>
        <Modal.Content centered>
          <Modal.Header>
            <Modal.Title>Add {relationType}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Select
              getOptionLabel={u => u.name}
              getOptionValue={u => u.id}
              isClearable
              isLoading={availableUsersLoading}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={(availableUsers || []).filter(u => !selectedUserIds.includes(u.id))}
              placeholder="Select user to add"
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              onChange={handleUserSelect}
            />
          </Modal.Body>
        </Modal.Content>
      </Modal>
    );
  }

  // Only the broker table (showAll) shows every user as a row; if it has none there's nothing to show.
  // The user table can legitimately start with zero rows (no links yet) and add via the picker.
  if (showAll && localUsers.length === 0) {
    return <p>No users found for this company.</p>;
  }


  const relationTitle = `${relationType} in Buildout`;

  return (
    <>
      <table className="table" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: '50%' }}>{relationTitle}</th>
            <th style={{ width: showAll ? '50%' : 'auto' }}>QuickBooks Vendor</th>
            {!showAll && <th style={{ width: '3rem'  }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((user) => {
            const currentUser = localUsers.find(u => u.id === user.userId);
            const currentVendor = vendorsById[currentUser?.quickbooksId];

            return (
              <tr key={user.id}>
                <td
                  className="align-middle"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {currentUser?.name || ''}
                </td>
                <td>
                  {vendorsLoading ? (
                    // Render the same Select control (disabled + loading) as a placeholder so the row keeps
                    // its full height while the vendor map loads, instead of collapsing to a bare spinner.
                    <Select aria-label="Loading vendor" isDisabled isLoading options={[]} value={null} />
                  ) : (
                    <QuickbooksSelect
                      buildoutObject={currentUser}
                      hydrated={!vendorsError}
                      initialValue={currentVendor}
                      quickbooksId={currentUser?.quickbooksId}
                      type="vendor"
                      onChange={vendor => handleVendorChange(currentUser, vendor)}
                    />
                  )}
                </td>
                {!showAll && (
                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      {currentUser && (
                        <FontAwesomeIcon
                          className="clickable text-destructive"
                          icon={faTrashAlt}
                          onClick={() => removeRow(user.id)}
                        />
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {!showAll && canAddUsers && (
            <tr>
              <td colSpan={3}>
                <AddControl onClick={() => setShowUserSelectModal(true)}>
                  Add Associations
                </AddControl>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {showUserSelectModal && renderUserSelectModal()}
    </>
  );
}

VendorRelationTable.propTypes = {
  relationType: PropTypes.string.isRequired,
  showAll: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    quickbooksId: PropTypes.number,
  })).isRequired,
  // Total non-broker user count, used by the user table to know whether more users can be added.
  usersCount: PropTypes.number,
  // Which linked-vendor set the async endpoint should resolve for these rows.
  vendorFetchType: PropTypes.oneOf(['broker', 'user']).isRequired,
};
